import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../../shared/api'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PcaInvoiceHeader {
  vertragsnummer:    9701 | 125103
  rechnungsnummer:   string
  leistungsmonat:    string  // ISO 'YYYY-MM-DD'
  rechnungsdatum:    string | null
  format_variant:    'A' | 'B' | 'C' | 'D'
  parse_confidence:  number  // 0..1
  netto:             string  // string-decimal
  mwst:              string
  brutto:            string
  total_essen_count: number
}

export type PcaLineType = 'verwaltungsgebuehr' | 'porto' | 'versand' | 'mailing'
export type PcaResolutionMethod = 'kennzeichen_exact' | 'name_fuzzy' | 'unresolved' | 'central_verwaltung'

/** Parse-time line shape (used inside PcaInvoicePreview / import wizard). */
export interface PcaImportLine {
  line_type:           PcaLineType
  schule_text:         string | null
  kennzeichen:         string | null
  essen_count:         number | null
  unit_price:          string
  line_net:            string
  /** fm_school_id string — matches useSchulen().data[*].id for direct dropdown binding. */
  school_id_resolved:  string | null
  resolution_method:   PcaResolutionMethod
}

export interface PcaIdempotencyConflict {
  existing_invoice_id:  number
  existing_imported_at: string  // ISO datetime
  existing_state:       string
}

export interface PcaInvoicePreview {
  preview:                true
  invoice:                PcaInvoiceHeader
  lines:                  PcaImportLine[]
  warnings:               string[]
  idempotency_conflict:   PcaIdempotencyConflict | null
  sha256:                 string
}

export interface PcaInvoiceCommit {
  preview:                false
  invoice_id:             number
  state:                  'committed'
  lines_inserted:         number
  unresolved_kennzeichen: string[]
}

export type PcaUploadResult = PcaInvoicePreview | PcaInvoiceCommit

// ── Phase 3 Types ─────────────────────────────────────────────────────────────

export interface PcaInvoiceSummary {
  invoice_id:        number
  vertragsnummer:    9701 | 125103
  leistungsmonat:    string
  rechnungsnummer:   string
  format_variant:    'A' | 'B' | 'C' | 'D'
  netto:             string
  mwst:              string
  brutto:            string
  total_essen_count: number | null
  state:             string
  imported_at:       string
  imported_by:       string
  line_count:        number
}

export interface PcaInvoiceLine {
  line_id:              number
  line_type:            'verwaltungsgebuehr' | 'porto' | 'versand' | 'mailing'
  schule_text:          string | null
  kennzeichen:          string | null
  school_id:            number | null
  essen_count:          number | null
  unit_price:           string
  line_net:             string
  resolution_method:    string | null
  format_a_aggregate:   boolean
}

export interface PcaInvoiceDetail {
  invoice: PcaInvoiceSummary
  lines:   PcaInvoiceLine[]
}

export type PcaSeverity =
  | 'MATCH' | 'TOLERANZ' | 'ABWEICHUNG' | 'MATERIAL'
  | 'FEHLEND_IN_DB' | 'FEHLEND_IM_RECHNUNG'
  | 'DUPLIKAT' | 'MAPPING_OFFEN' | 'PLATZHALTER' | 'FORMAT_A_AGGREGAT'

export interface PcaReconcileRow {
  school_id:       number | null
  school_name:     string | null
  kennzeichen:     string | null
  vertragsnummer:  9701 | 125103
  leistungsmonat:  string
  invoice:         { essen_count: number; netto: string; line_id: number } | null
  db:              { essen_count: number; netto: string } | null
  delta_netto:     string
  severity:        PcaSeverity
}

export interface PcaReconcileResponse {
  period:    { year: number; month: number | null }
  ytdTotal:  { netto_diff: string; by_severity: Record<PcaSeverity, number> }
  perSchool: PcaReconcileRow[]
}

export interface PcaSyncStatus {
  last_synced_at:       string | null
  max_booking_id_seen:  string | null
  row_count:            number
  age_hours:            number | null
}

export interface PcaSyncRefreshResult {
  /** 202 ack: the sync was enqueued to the background worker (not run inline).
   *  Completion is confirmed by polling the coverage watermark, NOT this body. */
  queued?:           boolean
  sources?:          PcaIncomeSource[]
  month?:            string
  /** Echoed back so callers fanning out parallel calls can correlate. */
  vertrag?:          9701 | 125103
  source?:           PcaIncomeSource
  provider?:         string
  /** false when this partition failed; error carries the reason. (legacy inline path) */
  success?:          boolean
  error?:            string
  rowsFetched?:      number
  rowsUpserted?:     number
  rowsMapped?:       number
  rowsUnmapped?:     number
  newMaxBookingId?:  string
  newMaxId?:         string
  lastSyncedAt?:     string
}

export interface PcaDbBookingRow {
  agency_id:          number
  agency_shortname:   string
  school_kennzeichen: string | null
  school_id:          number | null
  essen_count:        number
  netto_string:       string
  brutto_string:      string
  refreshed_at:       string
}

export interface PcaInvoiceListFilter   { year?: number; vertrag?: 9701 | 125103 }
export interface PcaReconcileFilter     { year: number; month?: number }
export interface PcaDbBookingsFilter    { year: number; month: number; vertrag: 9701 | 125103 }

// ── PCA Income (AuswertungPage Einnahmen Essen + Sonstige) ───────────────────

/** PCA-only source: 'essen' (Menü) or 'sonstiges' (Mahngeb. / Chipgeb. / Korrekturen). */
export type PcaIncomeSource = 'essen' | 'sonstiges'

export interface PcaIncomeBySchoolRow {
  school_id:     string   // dim.School.school_id (BIGINT stringified) — DB-side canonical
  fm_school_id:  string   // FM_SchoolId — matches useSchulen().id and Kostenstelle.schoolId
  school_name:   string

  // Essen (Menü via dbo.booking, CHARGE_ID 37/63/69, provider 'pca-booking')
  essen_count:           number
  /** Distinct JOURNAL_IDs across the month for this school — true "Buchungen"
   *  count (one customer transaction even when it produces multiple base_item
   *  lines). Prefer this over essen_count for "X Buchungen" labels. */
  essen_journal_count?:  number
  essen_netto:           string
  essen_brutto:          string

  // Sonstige (Mahngeb. / Chipgeb. / Korrekturen via dbo.booking, CHARGE_ID 2/18/20/28/38)
  sonstiges_count:           number
  sonstiges_journal_count?:  number
  sonstiges_netto:           string
  sonstiges_brutto:          string

  /** Verwaltungsgebühr slice of sonstiges (charge 18/20) — a real MC expense to
   *  PCA, NEGATIVE netto. Surfaced separately so the deep-dive shows it as a
   *  cost section instead of silently reducing Einnahmen. Optional until the
   *  Functions deploy that adds it. */
  verwaltung_netto?:  string
  verwaltung_brutto?: string

  /** Kiosk income (SAP Customer Checkout / DSFinV-K), from cache.SapKasseAggregate.
   *  netto/brutto are euro string-decimals; bar/unbar are BRUTTO split by payment
   *  type. Default '0.00' when the school has no kiosk in the period. */
  kiosk_netto?:        string
  kiosk_brutto?:       string
  kiosk_bar_brutto?:   string
  kiosk_unbar_brutto?: string

  /** Combined kitchen row — present when grouped=true and this row is a kitchen owner.
   *  school_name on this row is the kitchen label (e.g. "AVH + Lessing"). */
  kitchen_id?: number
  /** Per-member income breakdown — only when kitchen_id is set. */
  members?: Array<{
    school_id:    string  // dim.School.school_id
    school_name:  string
    is_owner:     boolean
    essen_netto:  string
    kiosk_brutto: string
  }>

  // back-compat aliases (deprecated, drop in v2) — equal to essen_netto / essen_brutto
  netto_string:       string
  brutto_string:      string
}

export interface PcaIncomeUnmappedRow {
  vertragsnummer:   9701 | 125103
  source:           PcaIncomeSource
  provider:         string
  agency_id:        string
  agency_shortname: string
  row_count:        number
  netto_string:     string
}

export interface PcaIncomeMissingSyncEntry {
  vertragsnummer: 9701 | 125103
  source:         PcaIncomeSource
}

export interface PcaIncomeBySchoolResponse {
  period:    string                                // 'YYYY-MM'
  vertrag:   9701 | 125103 | null
  perSchool: PcaIncomeBySchoolRow[]
  total: {
    essen_count:        number
    essen_netto:        string
    essen_brutto:       string
    sonstiges_count:    number
    sonstiges_netto:    string
    sonstiges_brutto:   string
    kiosk_netto?:       string
    kiosk_brutto?:      string
    // back-compat aliases (deprecated)
    netto_string:       string
    brutto_string:      string
  }
  unmapped:      PcaIncomeUnmappedRow[]
  missing_sync:  {
    entries:         PcaIncomeMissingSyncEntry[]
    vertragsnummern: number[]                     // back-compat: Essen-missing vertragsnummern
    leistungsmonat:  string
  } | null
}

// ── PCA Sync Coverage (import-page month grid) ───────────────────────────────

export interface PcaSyncCoverageSource {
  provider:       string
  last_synced_at: string
  max_id_seen:    string
  row_count:      number
  age_hours:      number | null
}

export interface PcaSyncCoverageMonth {
  leistungsmonat: string                          // 'YYYY-MM-DD'
  sources: Partial<Record<PcaIncomeSource, PcaSyncCoverageSource>>
}

export interface PcaSyncCoverageResponse {
  vertrag: 9701 | 125103
  from:    string                                 // 'YYYY-MM'
  to:      string
  months:  PcaSyncCoverageMonth[]
}

// ── Query keys ────────────────────────────────────────────────────────────────

const pcaInvoicesKey = (year?: number, vertrag?: number) =>
  ['pca', 'invoices', year ?? null, vertrag ?? null] as const

// ── Hooks ─────────────────────────────────────────────────────────────────────

/**
 * Upload a PCA invoice PDF (raw bytes, application/pdf).
 * dryRun=true  → preview response (PcaInvoicePreview, preview: true)
 * dryRun=false → commit response  (PcaInvoiceCommit,  preview: false)
 * override=true → pass &override=1 to allow overwriting a previously committed invoice
 *
 * On a successful commit, invalidates the ['pca', 'invoices', ...] query key so
 * the reconciliation hub list stays fresh.
 */
export function useUploadPcaInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, dryRun, override }: {
      file:     File
      dryRun:   boolean
      override?: boolean
    }): Promise<PcaUploadResult> => {
      const buf = await file.arrayBuffer()
      const qs = new URLSearchParams()
      qs.set('dryRun', dryRun ? '1' : '0')
      if (override) qs.set('override', '1')
      const url = `/api/imports/pca/invoice/upload?${qs.toString()}`
      const res = await window.fetch(url, {
        method:      'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/pdf',
          'X-Filename':   file.name,
        },
        body: buf,
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw Object.assign(new Error(json.error || `HTTP ${res.status}`), json)
      return json as PcaUploadResult
    },
    onSuccess: (data) => {
      // Only invalidate the list on a real commit (not a dry-run preview)
      if ('preview' in data && data.preview === false) {
        qc.invalidateQueries({ queryKey: ['pca', 'invoices'] })
      }
    },
  })
}

// ── Phase 3 Hooks ─────────────────────────────────────────────────────────────

/**
 * List PCA invoices with optional year / vertrag filter.
 * Query key: ['pca', 'invoices', year, vertrag]
 */
export function usePcaInvoiceList(filter?: PcaInvoiceListFilter) {
  const qs = new URLSearchParams()
  if (filter?.year)    qs.set('year',    String(filter.year))
  if (filter?.vertrag) qs.set('vertrag', String(filter.vertrag))
  const qsStr = qs.toString()

  return useQuery({
    queryKey: pcaInvoicesKey(filter?.year, filter?.vertrag),
    queryFn:  () =>
      api.get<{ invoices: PcaInvoiceSummary[] }>(
        `/api/pca/invoices${qsStr ? '?' + qsStr : ''}`,
      ),
    staleTime: 30_000,
  })
}

/**
 * Single-invoice detail (header + lines).
 * Disabled when invoiceId is null.
 * Query key: ['pca', 'invoice-detail', invoiceId]
 */
export function usePcaInvoiceDetail(invoiceId: number | null) {
  return useQuery({
    queryKey: ['pca', 'invoice-detail', invoiceId],
    queryFn:  () => api.get<PcaInvoiceDetail>(`/api/pca/invoices/${invoiceId}`),
    enabled:  typeof invoiceId === 'number' && invoiceId > 0,
    staleTime: 60_000,
  })
}

/**
 * Sync status for a given vertrag + month + optional source.
 * Default source = 'essen' (back-compat). source = 'all' returns array.
 * Query key: ['pca', 'sync-status', vertrag, month, source]
 */
export function usePcaSyncStatus(args: {
  vertrag: 9701 | 125103
  month:   string
  source?: PcaIncomeSource | 'all'
}) {
  const source = args.source ?? 'essen'
  const qs = new URLSearchParams({
    vertrag: String(args.vertrag),
    month:   args.month,
    source,
  })
  return useQuery({
    queryKey: ['pca', 'sync-status', args.vertrag, args.month, source],
    queryFn:  () => api.get<PcaSyncStatus>(`/api/pca/sync/status?${qs.toString()}`),
    staleTime: 60_000,
  })
}

// ── Delta-Sync pre-check ──────────────────────────────────────────────────────

export interface PcaSyncPrecheckPartition {
  vertrag: 9701 | 125103
  source: PcaIncomeSource
  monthIso: string          // 'YYYY-MM-01'
  match: boolean
  reasons: string[]         // e.g. ['neue Buchungen'], ['Zeilenzahl abweichend']
  src:   { rows: number; netto: number; maxId: string }
  cache: { rows: number; netto: number; maxIdSeen: string | null; lastSyncedAt: string | null }
}

export interface PcaSyncPrecheckResult {
  from: string
  to: string
  checkedAt: string
  partitions: PcaSyncPrecheckPartition[]
}

/**
 * DB-wide Delta-Sync pre-check: compares the source PCA DB (MAX(BOOKING_ID) vs
 * our watermark max_id_seen, plus row/netto totals vs cache) for EVERY
 * partition in the range. Mutation (not query): it wakes the serverless source
 * DB, so it only runs on explicit user action.
 *
 * Gateway-timeout hardening: the SWA gateway kills responses at ~60 s while
 * the function completes server-side (cold source DB: ~100 s for the combined
 * check). We therefore (a) split into one request per Vertrag (~halves each
 * request) and (b) retry each request up to 3× — the failed first attempt IS
 * the wake-up call; the retry hits a warm DB and finishes well under the limit.
 */
export type PcaPrecheckMode = 'seek' | 'full'

async function precheckOneVertrag(vertrag: 9701 | 125103, from: string, to: string, mode: PcaPrecheckMode): Promise<PcaSyncPrecheckResult> {
  const qs = new URLSearchParams({ from, to, vertrag: String(vertrag), mode })
  let lastErr: unknown
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      return await api.get<PcaSyncPrecheckResult>(`/api/pca/sync/precheck?${qs.toString()}`)
    } catch (e) {
      lastErr = e
      if (attempt < 3) await new Promise(r => setTimeout(r, 8000))
    }
  }
  throw lastErr
}

/** Plain (non-hook) DB-wide precheck — used by the global pcaSyncStore so the
 *  run survives route changes. mode='seek' (default) reads ONLY rows past our
 *  BOOKING_ID cursor (seconds); mode='full' is the grand sum+count integrity
 *  check that also catches deletions/in-place edits (Tiefenprüfung). */
export async function runPcaPrecheck(from: string, to: string, mode: PcaPrecheckMode = 'seek'): Promise<PcaSyncPrecheckResult> {
  const [a, b] = await Promise.all([
    precheckOneVertrag(9701, from, to, mode),
    precheckOneVertrag(125103, from, to, mode),
  ])
  return { ...a, partitions: [...a.partitions, ...b.partitions] }
}

/** Plain (non-hook) enqueue — used by the global pcaSyncStore. */
export async function postPcaSyncRefresh(args: {
  vertrag: 9701 | 125103
  month: string
  source: PcaIncomeSource | 'all'
}): Promise<PcaSyncRefreshResult> {
  const qs = new URLSearchParams({ vertrag: String(args.vertrag), month: args.month, source: args.source })
  const res = await window.fetch(`/api/pca/sync/refresh?${qs.toString()}`, {
    method: 'POST',
    credentials: 'include',
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw Object.assign(new Error(json.error || `HTTP ${res.status}`), json)
  return json as PcaSyncRefreshResult
}

/** Plain (non-hook) coverage fetch — the store's watermark poll. */
export async function fetchPcaSyncCoverage(vertrag: 9701 | 125103, from: string, to: string): Promise<PcaSyncCoverageResponse> {
  const qs = new URLSearchParams({ vertrag: String(vertrag), from, to })
  return api.get<PcaSyncCoverageResponse>(`/api/pca/sync/coverage?${qs.toString()}`)
}

/**
 * Trigger a PCA-DB sync for a given vertrag + month + optional source.
 * source defaults to 'essen' (back-compat). 'all' runs essen + sonstiges sequentially.
 * On success, invalidates sync-status, coverage, db-bookings, income, and reconcile roots.
 */
export function usePcaSyncRefresh() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: {
      vertrag: 9701 | 125103
      month:   string
      source?: PcaIncomeSource | 'all'
    }): Promise<PcaSyncRefreshResult> => {
      const source = args.source ?? 'essen'
      const qs = new URLSearchParams({
        vertrag: String(args.vertrag),
        month:   args.month,
        source,
      })
      const res = await window.fetch(`/api/pca/sync/refresh?${qs.toString()}`, {
        method:      'POST',
        credentials: 'include',
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw Object.assign(new Error(json.error || `HTTP ${res.status}`), json)
      return json as PcaSyncRefreshResult
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pca', 'sync-status'] })
      qc.invalidateQueries({ queryKey: ['pca', 'sync-coverage'] })
      // NOTE: invalidateQueries does element-by-element PREFIX matching, so
      // ['pca','sync-coverage'] does NOT match the chip-strip's key
      // ['pca','sync-coverage-multi',…] ('sync-coverage' !== 'sync-coverage-multi'
      // at index 1). Must invalidate it explicitly or the 12-month coverage strip
      // on PcaExplorerPage stays green-stale until a full page reload remounts it.
      qc.invalidateQueries({ queryKey: ['pca', 'sync-coverage-multi'] })
      qc.invalidateQueries({ queryKey: ['pca', 'db-bookings'] })
      qc.invalidateQueries({ queryKey: ['pca', 'income-by-school'] })
      qc.invalidateQueries({ queryKey: ['pca', 'income-by-school-category'] })
      qc.invalidateQueries({ queryKey: ['pca', 'income-by-school-day'] })
      qc.invalidateQueries({ queryKey: ['pca', 'reconcile'] })
    },
  })
}

/**
 * Per-(vertrag, month, source) sync state across a date range. Powers the
 * PCA Bookings Import page's month grid — colour pills, last_synced_at,
 * row_count for each (Essen, Bargeldlos) source per month.
 *
 * Query key: ['pca', 'sync-coverage', vertrag, from, to]
 */
export function usePcaSyncCoverage(args: {
  vertrag: 9701 | 125103
  from:    string  // 'YYYY-MM'
  to:      string  // 'YYYY-MM'
}) {
  const qs = new URLSearchParams({
    vertrag: String(args.vertrag),
    from:    args.from,
    to:      args.to,
  })
  return useQuery({
    queryKey: ['pca', 'sync-coverage', args.vertrag, args.from, args.to],
    queryFn:  () => api.get<PcaSyncCoverageResponse>(`/api/pca/sync/coverage?${qs.toString()}`),
    enabled:  /^\d{4}-\d{2}$/.test(args.from) && /^\d{4}-\d{2}$/.test(args.to),
    // Short staleTime so SyncFreshness reflects reality quickly after sync
    // (the freshness chip is the whole point — caching it isn't useful).
    staleTime: 5_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })
}

/**
 * Multi-Vertrag sync coverage: fans out to `/api/pca/sync/coverage` per
 * Vertrag and merges results into a single `{ [period]: { vertrag → sources } }`
 * map. Powers the 12-month coverage chip strip on PcaExplorerPage so a chip's
 * colour can reflect BOTH Verträge's state without a backend change.
 *
 * Implementation note: react-query's useQueries would be cleaner but adds an
 * import dependency. Two parallel single-key queries via Promise.all keeps the
 * hook self-contained and the cache keys stable.
 */
export interface PcaSyncCoverageMultiEntry {
  leistungsmonat: string                                       // 'YYYY-MM-01'
  byVertrag:      Map<number, Partial<Record<PcaIncomeSource, PcaSyncCoverageSource>>>
}
export interface PcaSyncCoverageMultiResult {
  from:           string
  to:             string
  vertrags:       number[]
  /** Keyed by leistungsmonat ('YYYY-MM-01'). */
  byMonth:        Map<string, PcaSyncCoverageMultiEntry>
}

export function usePcaSyncCoverageMulti(args: {
  vertrags: number[]
  from:     string  // 'YYYY-MM'
  to:       string  // 'YYYY-MM'
}) {
  const { vertrags, from, to } = args
  const enabled = /^\d{4}-\d{2}$/.test(from) && /^\d{4}-\d{2}$/.test(to) && vertrags.length > 0
  return useQuery({
    queryKey: ['pca', 'sync-coverage-multi', vertrags.slice().sort().join(','), from, to],
    enabled,
    // Mirror usePcaSyncCoverage: this powers the freshness chip strip, so a short
    // staleTime + refetch-on-focus/mount keeps the green pills honest even without
    // an explicit invalidation. (The sync mutation also invalidates it directly.)
    staleTime: 5_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<PcaSyncCoverageMultiResult> => {
      const responses = await Promise.all(
        vertrags.map(v => {
          const qs = new URLSearchParams({ vertrag: String(v), from, to })
          return api.get<PcaSyncCoverageResponse>(`/api/pca/sync/coverage?${qs.toString()}`)
        }),
      )
      const byMonth = new Map<string, PcaSyncCoverageMultiEntry>()
      for (let i = 0; i < vertrags.length; i++) {
        const v   = vertrags[i]
        const res = responses[i]
        for (const m of res.months) {
          let entry = byMonth.get(m.leistungsmonat)
          if (!entry) {
            entry = { leistungsmonat: m.leistungsmonat, byVertrag: new Map() }
            byMonth.set(m.leistungsmonat, entry)
          }
          entry.byVertrag.set(v, m.sources)
        }
      }
      return { from, to, vertrags, byMonth }
    },
  })
}

/**
 * Reconciliation diff engine — compares invoice lines vs PCA-DB aggregates.
 * Query key: ['pca', 'reconcile', year, month]
 */
export function usePcaReconciliation(filter: PcaReconcileFilter) {
  const qs = new URLSearchParams({ year: String(filter.year) })
  if (filter.month != null) qs.set('month', String(filter.month))
  return useQuery({
    queryKey: ['pca', 'reconcile', filter.year, filter.month ?? null],
    queryFn:  () => api.get<PcaReconcileResponse>(`/api/pca/reconcile?${qs.toString()}`),
    staleTime: 30_000,
  })
}

/**
 * Browse raw PCA-DB aggregates for a given year / month / vertrag.
 * Query key: ['pca', 'db-bookings', year, month, vertrag]
 */
export function usePcaDbBookings(filter: PcaDbBookingsFilter) {
  const qs = new URLSearchParams({
    year:    String(filter.year),
    month:   String(filter.month),
    vertrag: String(filter.vertrag),
  })
  return useQuery({
    queryKey: ['pca', 'db-bookings', filter.year, filter.month, filter.vertrag],
    queryFn:  () => api.get<{ rows: PcaDbBookingRow[] }>(`/api/pca/db/bookings?${qs.toString()}`),
    staleTime: 60_000,
  })
}

/**
 * Per-school income for one Leistungsmonat — drives the AuswertungPage
 * "Einnahmen Essen" column. Reads cache.PcaBookingAggregate only (no live PCA).
 * The response includes a `missing_sync` indicator for periods where the
 * cache hasn't been primed yet, and `unmapped[]` for agencies the fuzzy
 * matcher couldn't map to a dim.School yet.
 *
 * Query key: ['pca', 'income-by-school', period, vertrag ?? null]
 */
export function usePcaIncomeBySchool(args: { period: string | null; vertrag?: 9701 | 125103 | null; grouped?: boolean }) {
  const { period, grouped = true } = args
  return useQuery({
    queryKey: ['pca', 'income-by-school', period, args.vertrag ?? null, grouped],
    enabled: !!period && /^\d{4}-\d{2}$/.test(period),
    queryFn: () => {
      const qs = new URLSearchParams({ period: period! })
      if (args.vertrag) qs.set('vertrag', String(args.vertrag))
      if (!grouped) qs.set('grouped', '0')
      return api.get<PcaIncomeBySchoolResponse>(`/api/pca/income/by-school?${qs.toString()}`)
    },
    staleTime: 60_000,
  })
}

export interface PcaIncomeByDayRow {
  valuta_date:       string | null
  source:            PcaIncomeSource
  category:          string | null
  base_item:         string | null
  prefixes:          string  // csv ("Storno,Nachbuchung")
  tags:              string  // csv ("SoZ/BuT-Anteil,Zuschuss")
  row_count:         number
  /** Distinct JOURNAL_IDs for THIS (group, day). On S-side ~= row_count. */
  journal_count:     number
  /** Distinct JOURNAL_IDs across ALL sibling groups for (school, day, source).
   *  Duplicated across siblings — clients take MAX() per (date, source) for
   *  day-level totals, then SUM over days for month totals. */
  day_journal_count: number
  ordered_count:     number  // Essen only; 0 for Sonstige
  distributed_count: number  // Essen only; 0 for Sonstige
  netto:             string
  brutto:            string
}

export interface PcaIncomeByDayResponse {
  period:   string
  schoolId: number
  sources:  PcaIncomeSource[]
  rows:     PcaIncomeByDayRow[]
}

export interface PcaCategoryCell {
  source:              PcaIncomeSource
  category:            string
  base_item:           string
  prefixes:            string[]
  tags:                string[]
  count:               number
  /** Essen only — meals ordered (H-side row count). 0 for Sonstige. */
  ordered_count:       number
  /** Essen only — meals actually served (H-side DISTRIBUTED='Y'). 0 for Sonstige. */
  distributed_count:   number
  netto:               string
  brutto:              string
}
export interface PcaIncomeBySchoolCategoryRow {
  school_id:    string
  fm_school_id: string
  school_name:  string
  categories:   PcaCategoryCell[]
}
export interface PcaIncomeBySchoolCategoryResponse {
  period:    string
  sources:   PcaIncomeSource[]
  perSchool: PcaIncomeBySchoolCategoryRow[]
}

export function usePcaIncomeBySchoolCategory(args: {
  period:  string | null
  sources: PcaIncomeSource[]
}) {
  const { period, sources } = args
  return useQuery({
    queryKey: ['pca', 'income-by-school-category', period, sources.join(',')],
    enabled:  !!period && /^\d{4}-\d{2}$/.test(period) && sources.length > 0,
    queryFn:  () => {
      const qs = new URLSearchParams({ period: period!, sources: sources.join(',') })
      return api.get<PcaIncomeBySchoolCategoryResponse>(`/api/pca/income/by-school-category?${qs.toString()}`)
    },
    staleTime: 60_000,
  })
}

export function usePcaIncomeBySchoolDay(args: {
  period:   string | null
  schoolId: number | string | null
  sources:  PcaIncomeSource[]
  enabled?: boolean
}) {
  const { period, schoolId, sources, enabled = true } = args
  return useQuery({
    queryKey: ['pca', 'income-by-school-day', period, String(schoolId ?? ''), sources.join(',')],
    enabled:
      enabled &&
      !!period && /^\d{4}-\d{2}$/.test(period) &&
      schoolId != null && schoolId !== '' &&
      sources.length > 0,
    queryFn: () => {
      const qs = new URLSearchParams({
        period:   period!,
        schoolId: String(schoolId),
        sources:  sources.join(','),
      })
      return api.get<PcaIncomeByDayResponse>(`/api/pca/income/by-school-day?${qs.toString()}`)
    },
    staleTime: 60_000,
  })
}

/**
 * Acknowledge a reconciliation diff entry.
 * On success, invalidates ['pca', 'reconcile'] so the diff table refreshes.
 */
export function useAcknowledgePcaDiff() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: {
      invoice_line_id:  number
      ack_reason_code:  'LATE_BOOKING' | 'KNOWN_DISPUTE' | 'ROUNDING_ERROR' | 'MAPPING_PENDING' | 'ALLOCATION_DEFERRED' | 'OTHER'
      ack_notes?:       string
    }): Promise<{ rec_id: number }> =>
      api.post<{ rec_id: number }>('/api/pca/reconcile/ack', args),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pca', 'reconcile'] })
    },
  })
}
